import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { DollarSign, TrendingUp, Calendar, Download, ArrowUpRight } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';

const EarningsPage = () => {
  const { user } = useAuth();
  const [earnings, setEarnings] = useState({ total: 15420, thisMonth: 3850, pending: 1200, available: 2650 });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.getTimeEntries();
        const entries = data.entries || [];
        const txns = entries.map((e, i) => ({ id: e.entry_id || i, date: e.date, project: e.project, amount: Math.round(e.hours * 120), status: e.status === 'approved' ? 'paid' : 'pending' }));
        setTransactions(txns);
        const totalPaid = txns.filter(t => t.status === 'paid').reduce((s, t) => s + t.amount, 0);
        const totalPending = txns.filter(t => t.status === 'pending').reduce((s, t) => s + t.amount, 0);
        setEarnings({ total: totalPaid + totalPending + 10000, thisMonth: totalPaid + totalPending, pending: totalPending, available: totalPaid });
      } catch {
        setTransactions([
          { id: 1, date: '2025-01-28', project: 'AI Model Development', amount: 1200, status: 'paid' },
          { id: 2, date: '2025-01-25', project: 'Backend API Integration', amount: 850, status: 'paid' },
          { id: 3, date: '2025-01-20', project: 'Frontend Consulting', amount: 600, status: 'paid' },
          { id: 4, date: '2025-01-15', project: 'Code Review Services', amount: 400, status: 'paid' },
          { id: 5, date: '2025-01-10', project: 'System Architecture Design', amount: 800, status: 'paid' },
          { id: 6, date: '2025-01-05', project: 'Database Optimization', amount: 1200, status: 'pending' },
        ]);
      } finally { setLoading(false); }
    };
    load();
  }, [user]);

  return (
    <div className="flex min-h-screen bg-[#F9FBF5]">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-[70px] pt-[60px] md:pt-0 pb-[60px] md:pb-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
            <div><h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Earnings</h1><p className="text-gray-600">Track your payments and transaction history</p></div>
            <Button className="gap-2"><Download className="w-4 h-4" />Export Report</Button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
            <Card className="p-4 sm:p-6"><div className="flex items-center justify-between mb-2"><p className="text-xs sm:text-sm text-gray-600">Total Earnings</p><DollarSign className="w-5 h-5 text-green-600" /></div><p className="text-xl sm:text-3xl font-bold text-gray-900">${earnings.total.toLocaleString()}</p><p className="text-xs sm:text-sm text-green-600 mt-2 flex items-center gap-1"><TrendingUp className="w-4 h-4" />+12% from last month</p></Card>
            <Card className="p-4 sm:p-6"><div className="flex items-center justify-between mb-2"><p className="text-xs sm:text-sm text-gray-600">This Month</p><Calendar className="w-5 h-5 text-[#0A3900]" /></div><p className="text-xl sm:text-3xl font-bold text-gray-900">${earnings.thisMonth.toLocaleString()}</p><p className="text-xs sm:text-sm text-gray-500 mt-2">January 2025</p></Card>
            <Card className="p-4 sm:p-6"><div className="flex items-center justify-between mb-2"><p className="text-xs sm:text-sm text-gray-600">Pending</p><ArrowUpRight className="w-5 h-5 text-yellow-600" /></div><p className="text-xl sm:text-3xl font-bold text-gray-900">${earnings.pending.toLocaleString()}</p><p className="text-xs sm:text-sm text-gray-500 mt-2">Processing...</p></Card>
            <Card className="p-4 sm:p-6"><div className="flex items-center justify-between mb-2"><p className="text-xs sm:text-sm text-gray-600">Available</p><DollarSign className="w-5 h-5 text-[#0A3900]" /></div><p className="text-xl sm:text-3xl font-bold text-gray-900">${earnings.available.toLocaleString()}</p><Button size="sm" className="mt-2 w-full">Withdraw</Button></Card>
          </div>

          <Card className="p-4 sm:p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Earnings Trend</h2>
            <div className="h-48 sm:h-64 bg-gradient-to-t from-[#f0f5ed] to-transparent rounded-lg flex items-end justify-around px-2 sm:px-4 pb-4">
              {[65, 45, 80, 55, 90, 70, 100, 85, 75, 95, 88, 92].map((h, i) => (<div key={i} className="bg-[#0A3900] rounded-t w-4 sm:w-8 lg:w-12 hover:bg-[#1A5C0A] transition-colors cursor-pointer" style={{ height: `${h}%` }} />))}
            </div>
            <div className="flex justify-between mt-4 text-xs text-gray-600">{['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map(m => <span key={m}>{m}</span>)}</div>
          </Card>

          <Card>
            <div className="p-4 sm:p-6 border-b"><h2 className="text-xl font-semibold text-gray-900">Transaction History</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b"><tr>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 sm:px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr></thead>
                <tbody className="divide-y">
                  {transactions.map((t) => (
                    <tr key={t.id} className="hover:bg-gray-50">
                      <td className="px-4 sm:px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{t.date}</td>
                      <td className="px-4 sm:px-6 py-4 text-sm text-gray-900">{t.project}</td>
                      <td className="px-4 sm:px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">${t.amount}</td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap"><span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${t.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{t.status === 'paid' ? 'Paid' : 'Pending'}</span></td>
                      <td className="px-4 sm:px-6 py-4 text-right"><Button variant="ghost" size="sm">View</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EarningsPage;
